import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  validateImageFile, 
  processImage, 
  generateCategoryImageFilename,
  getImageDimensions
} from '@/utils/imageUtils';
import { toast } from 'sonner';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadedImage {
  url: string;
  path: string;
  filename: string;
  size: number;
}

export interface UseImageUploadOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  generateVariants?: boolean;
}

export interface ImageUploadState {
  isUploading: boolean;
  progress: UploadProgress | null;
  uploadedImage: UploadedImage | null;
  error: string | null;
  previewUrl: string | null;
}

export const useCategoryImageUpload = (options: UseImageUploadOptions = {}) => {
  const {
    maxWidth = 1200,
    maxHeight = 600,
    quality = 0.85,
    generateVariants = false
  } = options;

  const [state, setState] = useState<ImageUploadState>({
    isUploading: false,
    progress: null,
    uploadedImage: null,
    error: null,
    previewUrl: null
  });

  /**
   * Reset upload state
   */
  const resetUpload = useCallback(() => {
    if (state.previewUrl) {
      URL.revokeObjectURL(state.previewUrl);
    }
    setState({
      isUploading: false,
      progress: null,
      uploadedImage: null,
      error: null,
      previewUrl: null
    });
  }, [state.previewUrl]);

  /**
   * Set preview for selected file
   */
  const setPreview = useCallback((file: File) => {
    // Clean up previous preview
    if (state.previewUrl) {
      URL.revokeObjectURL(state.previewUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    setState(prev => ({
      ...prev,
      previewUrl,
      error: null
    }));
  }, [state.previewUrl]);

  /**
   * Upload image to Supabase storage
   */
  const uploadImage = useCallback(async (
    file: File, 
    categoryName: string
  ): Promise<UploadedImage | null> => {
    try {
      // Check authentication first
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        const errorMsg = 'You must be logged in to upload images';
        setState(prev => ({ ...prev, error: errorMsg }));
        toast.error(errorMsg);
        return null;
      }

      console.log('🔐 Auth check passed:', {
        userId: session.user.id,
        email: session.user.email,
        hasAccessToken: !!session.access_token,
        tokenExpiry: session.expires_at
      });

      // Refresh session if it's close to expiring (within 5 minutes)
      const expiresAt = session.expires_at ? new Date(session.expires_at * 1000) : null;
      const now = new Date();
      const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
      
      if (expiresAt && expiresAt < fiveMinutesFromNow) {
        console.log('🔄 Refreshing session before upload...');
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshData.session) {
          const errorMsg = 'Session expired. Please log in again.';
          setState(prev => ({ ...prev, error: errorMsg }));
          toast.error(errorMsg);
          return null;
        }
      }

      // Validate file
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        setState(prev => ({ ...prev, error: validation.error || 'Invalid file' }));
        toast.error(validation.error || 'Invalid file');
        return null;
      }

      // Get image dimensions for validation
      try {
        const dimensions = await getImageDimensions(file);
        // Ensure minimum dimensions for category images
        if (dimensions.width < 300 || dimensions.height < 200) {
          const error = 'Image must be at least 300x200 pixels';
          setState(prev => ({ ...prev, error }));
          toast.error(error);
          return null;
        }
      } catch (error) {
        console.warn('Could not get image dimensions:', error);
      }

      setState(prev => ({
        ...prev,
        isUploading: true,
        progress: { loaded: 0, total: file.size, percentage: 0 },
        error: null
      }));

      // Process image to optimize for category display
      const processedImage = await processImage(file, {
        maxWidth,
        maxHeight,
        quality,
        format: 'jpeg' // Ensure consistent format for category images
      });

      // Generate filename
      const filename = generateCategoryImageFilename(categoryName, 'card');

      console.log('📤 Upload details:', {
        filename,
        fileSize: processedImage.size,
        fileType: processedImage.type,
        bucketName: 'category-images'
      });

      // Get current session for debugging
      const { data: currentSession } = await supabase.auth.getSession();
      console.log('🔑 Current session state:', {
        hasSession: !!currentSession.session,
        hasAccessToken: !!currentSession.session?.access_token,
        userEmail: currentSession.session?.user?.email
      });

      // Upload processed image to Supabase storage
      let uploadResult = await supabase.storage
        .from('category-images')
        .upload(filename, processedImage, {
          cacheControl: '3600',
          upsert: true, // Allow overwriting existing images
          contentType: 'image/jpeg'
        });

      // If upload fails with 400/403 error, try service role fallback
      if (uploadResult.error?.message?.includes('400') || 
          uploadResult.error?.message?.includes('403') || 
          uploadResult.error?.message?.includes('Bad Request') ||
          uploadResult.error?.message?.includes('Unauthorized') ||
          uploadResult.error?.message?.includes('row-level security policy')) {
        
        console.log('🔄 Upload failed with RLS/Auth error, trying service role fallback...');
        
        // Try service role client for admin uploads
        const { createClient } = await import('@supabase/supabase-js');
        
        // Check if we have service role key available (admin users only)
        const serviceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY || 
                              import.meta.env.SUPABASE_SERVICE_ROLE_KEY;
        
        if (serviceRoleKey && currentSession.session?.user?.email === 'joey@towb.in') {
          console.log('🔑 Using service role for admin upload...');
          
          const serviceClient = createClient(
            import.meta.env.VITE_SUPABASE_URL!,
            serviceRoleKey,
            {
              auth: {
                persistSession: false
              }
            }
          );

          uploadResult = await serviceClient.storage
            .from('category-images')
            .upload(filename, processedImage, {
              cacheControl: '3600',
              upsert: true,
              contentType: 'image/jpeg'
            });

          console.log('🔑 Service role upload result:', uploadResult);
        } else {
          console.log('⚠️ Service role not available, trying explicit auth headers...');
          
          // Fallback to explicit auth headers
          const explicitAuthClient = createClient(
            import.meta.env.VITE_SUPABASE_URL!,
            import.meta.env.VITE_SUPABASE_ANON_KEY!,
            {
              global: {
                headers: {
                  Authorization: `Bearer ${currentSession.session?.access_token}`,
                  'Accept': 'application/json',
                  'Cache-Control': 'no-cache'
                }
              }
            }
          );

          uploadResult = await explicitAuthClient.storage
            .from('category-images')
            .upload(filename, processedImage, {
              cacheControl: '3600',
              upsert: true,
              contentType: 'image/jpeg'
            });

          console.log('🔑 Explicit auth upload result:', uploadResult);
        }
      }

      const { data, error } = uploadResult;

      if (error) {
        console.error('❌ Final upload error:', error);
        throw error;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('category-images')
        .getPublicUrl(data.path);

      const uploadedImage: UploadedImage = {
        url: urlData.publicUrl,
        path: data.path,
        filename: data.path.split('/').pop() || filename,
        size: processedImage.size
      };

      setState(prev => ({
        ...prev,
        isUploading: false,
        progress: null,
        uploadedImage,
        error: null
      }));

      toast.success('Image uploaded successfully!');
      return uploadedImage;

    } catch (error: any) {
      // Provide user-friendly error messages
      let userMessage = 'Failed to upload image';
      
      if (error.message?.includes('network') || error.message?.includes('fetch')) {
        userMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message?.includes('401') || error.message?.includes('unauthorized')) {
        userMessage = 'Authentication error. Please log in again.';
      } else if (error.message?.includes('403') || error.message?.includes('forbidden')) {
        userMessage = 'Permission denied. Please check your account permissions.';
      } else if (error.message?.includes('413') || error.message?.includes('too large')) {
        userMessage = 'Image file is too large. Please try a smaller image.';
      } else if (error.message?.includes('already exists')) {
        // This shouldn't happen with upsert: true, but handle it just in case
        userMessage = 'Image upload conflict. Please try again.';
      } else if (error.message?.includes('Invalid file name format')) {
        userMessage = 'Invalid filename. Please try a different category name.';
      }
      
      setState(prev => ({
        ...prev,
        isUploading: false,
        progress: null,
        error: userMessage
      }));

      toast.error(userMessage);
      return null;
    }
  }, [maxWidth, maxHeight, quality]);

  /**
   * Delete image from storage
   */
  const deleteImage = useCallback(async (path: string): Promise<boolean> => {
    try {
      const { error } = await supabase.storage
        .from('category-images')
        .remove([path]);

      if (error) {
        throw error;
      }

      setState(prev => ({
        ...prev,
        uploadedImage: null
      }));

      toast.success('Image deleted successfully');
      return true;

    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error('Failed to delete image');
      return false;
    }
  }, []);

  /**
   * Handle file selection/drop
   */
  const handleFileSelect = useCallback(async (
    file: File,
    categoryName: string,
    autoUpload: boolean = false
  ) => {
    // Set preview immediately
    setPreview(file);

    // Auto-upload if requested
    if (autoUpload && categoryName) {
      await uploadImage(file, categoryName);
    }
  }, [setPreview, uploadImage]);

  return {
    // State
    ...state,
    
    // Actions
    uploadImage,
    deleteImage,
    resetUpload,
    setPreview,
    handleFileSelect,
    
    // Computed values
    isReady: !state.isUploading && !state.error,
    hasImage: !!state.uploadedImage,
    hasPreview: !!state.previewUrl,
    progressPercentage: state.progress?.percentage || 0
  };
};